import React, { useEffect, useState } from 'react';
import { SdfBox, SdfCarousel, SdfCarouselItem, SdfDatePicker, SdfQuickStat } from '@synerg/react-components';
import 'react-datepicker/dist/react-datepicker.css';
import { values, get } from 'lodash';
import Placeholder from '../../common/placeholder';
import { drawPieChart } from './charts';
import { ColorList } from './colors';
import moment from 'moment-timezone';

const getDateInYearMonth = (date) => {
	const check = moment(date, 'YYYY/MM/DD');
	const month = check.format('MM');
	const year = check.format('YYYY');
	return year + month;
};

const monthDiff = (dateFrom, dateTo) => {
	return dateTo.getMonth() - dateFrom.getMonth() + 
		(12 * (dateTo.getFullYear() - dateFrom.getFullYear()));
};

export default function UsageByVertical(props) {
	const { data = [], getApiUsageData, timelineNumber = 0, loading = false, isAdminView = false } = props;
	const [selectedDate, setSelectedDate] = useState(new Date());
	const [selectedDateString, setSelectedDateString] = useState('');
	const [dataByMonthYear, setDataByMonthYear] = useState([]);

	useEffect(() => {
		if (data.length > 0) {
			getDataBySelectedMonthYear();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, selectedDateString]);

	useEffect(() => {
		if (dataByMonthYear.length > 0) {
			drawPieChart(dataByMonthYear, isAdminView);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataByMonthYear]);

	const getDataBySelectedMonthYear = () => {
		const dataByMonthAndYear = data.reduce((acc, vertical) => {
			if (vertical.year_month === selectedDateString) {
				if (acc[vertical.industry]) {
					acc[vertical.industry].volume += vertical.volume;
				} else {
					acc[vertical.industry] = { ...vertical };
				}
			}
			return acc;
		}, {});
		const sortedData = values(dataByMonthAndYear).sort((a, b) => {
			return b.volume - a.volume;
		});
		const sortedDataWithColor = sortedData.map((vertical, index) => {
			if (index !== 0 && (index % ColorList.length === 0) && (sortedData.length === ColorList.length + 1)) {
				return { ...vertical, color: ColorList[(index + 1) % ColorList.length] };
			} else {
				return { ...vertical, color: ColorList[index % ColorList.length] };
			}
		});
		setDataByMonthYear(sortedDataWithColor);
	};

	const onChangeDate = (date) => {
		setSelectedDate(date);
		const yearMonth = getDateInYearMonth(date);
		setSelectedDateString(yearMonth);
		if (monthDiff(new Date(date), new Date()) > timelineNumber) {
			getApiUsageData(monthDiff(new Date(date), new Date()));
		}
	};

	const getVerticalDataByMonth = (verticalName, monthAndYear) => {
		const verticalData = data.reduce((acc, vertical) => {
			if (vertical.industry === verticalName && vertical.year_month === monthAndYear) {
				if (acc[verticalName]) {
					acc[verticalName].volume += vertical.volume;
				} else {
					acc[verticalName] = { ...vertical };
				}
			}
			return acc;
		}, {})
		return verticalData[verticalName];
	};

	const Carousel = ({ dataByMonthYear = [] }) => {
		return (<SdfCarousel className="max-w-full" style={{ maxWidth: '500px' }} activeId="carouselItem-0">
			{dataByMonthYear.map((row, i) => (
				<CarouselItem key={`carouselItem-${i}`} row={row} index={i} />
			))}
		</SdfCarousel>)
	}

	const CarouselItem = ({ row, index }) => {
		const selectedVerticalObj = {};
		const month = parseInt(selectedDateString.slice(4));
		const year = parseInt(selectedDateString.slice(0, 4));
		let lastMonth = month - 1;
		let lastYear = year;
		if (lastMonth === 0) {
			lastMonth = 12;
			lastYear = year - 1;
		}
		if (lastMonth < 10) {
			lastMonth = '0' + lastMonth.toString();
		}
		const lastMonthYear = lastYear.toString() + lastMonth.toString();
		selectedVerticalObj['thisMonth'] = getVerticalDataByMonth(row.industry, selectedDateString);
		selectedVerticalObj['lastMonth'] = getVerticalDataByMonth(row.industry, lastMonthYear);
		return (
			<SdfCarouselItem id={index} className="max-w-full">
				<div className="flex justify-center w-full">
					<SdfBox className='w-4/5 mb-8'>
						<div className='flex justify-center text-heading-05 font-bold'>{get(selectedVerticalObj, 'thisMonth.industry', '-')}</div>
						<div className="flex justify-center text-heading-02 font-bold">API Calls</div>
						<div className='flex gap-10 justify-center mt-5'>
							<SdfQuickStat
								label="This Month"
								size="sm"
								labelPosition="before"
							>{get(selectedVerticalObj, 'thisMonth.volume', '-').toLocaleString()}</SdfQuickStat>
							<SdfQuickStat
								label="Last Month"
								size="sm"
								labelPosition="before"
							>{get(selectedVerticalObj, 'lastMonth.volume', '-').toLocaleString()}</SdfQuickStat>
						</div>
					</SdfBox>
				</div>
			</SdfCarouselItem>)
	};


	return (
		<div className='api-usage'>
			<div className='api-usage'>
				<div className='chart-container'>
					<div className='flex justify-between items-center'>
						<div className='title-section'>
							<div className='text-heading-05 font-bold'>
								API Usage
							</div>
							<div className='text-heading-02 font-bold'>
								By vertical
							</div>
						</div>
						<SdfDatePicker
							className="flex"
							value={selectedDate}
							onSdfChange={({ detail }) => onChangeDate(detail)}
							dateFormat='short'
							rules={[{
								type: "DISABLED",
								startDateInclusive: new Date().setDate(1)
							}]}
						/>
					</div>
					{loading ? <div className='placeholder'>
						<Placeholder height={400} />
					</div> :
						<div>{data.length > 0 && dataByMonthYear && dataByMonthYear.length > 0 ? <div className={isAdminView ? 'admin-view' : ''}>
							<div className="flex justify-between m-5">
								<div id='pie-container' />
								<div className="flex-1">
									<div className="flex justify-end text-heading-01 font-bold">
										API calls
									</div>
									{dataByMonthYear.map((row, i) => (
										<div key={`dataByMonthYear-${i}`} className="flex border-0 border-b-1  border-dark border-solid p-2 m-2 gap-5 justify-between">
											<div className="industry flex items-center"><div className='mr-1' style={{ backgroundColor: row.color, height: '9px', width: '9px', borderRadius: '50%' }}> </div> {row.industry} </div>
											<div className="industry"> {row.volume} </div>
										</div>
									))}
								</div>
							</div>
						</div> :
							<div className='flex min-h-md justify-center items-center'>No chart available for selected date</div>}
						</div>}
				</div>
				<div>
					{loading ? <div className='placeholder'>
						<Placeholder height={100} />
					</div> :
						<div className="max-w-full">{data.length > 0 && dataByMonthYear && dataByMonthYear.length > 0 &&
							<Carousel dataByMonthYear={dataByMonthYear} />}
						</div>}
				</div>
			</div>
		</div>
	);
}
